import type {
  IntakeAuthoringTargetShape,
  IntakeExposureHint,
  IntakeOrientationHint,
  IntakePointLabelIntent,
  IntakeQuestionTypeHint,
  IntakeRowSourceHint,
  IntakeTargetShape,
  StudyEvidenceConstraint,
  StudyEvidenceContract,
  StudyEvidenceRequirement,
} from "./index";
import type { PlanVerbatimContent } from "./plan";
import { TESTED_ASSET_TYPE_IDS } from "./registry";

// A stored constraint's `targetShape` and `verbatimContent` (closed shapes:
// optional fields listed, extra keys refused). The Python mirror is
// `IntakeAuthoringTargetShape` and `PlanVerbatimContent` in
// `models/questionnaire/contracts.py`; `test_study_evidence_contracts.py`
// holds the field and vocabulary lists to it.
const TARGET_SHAPE_FIELDS = [
  "questionType",
  "questionCount",
  "columnCount",
  "measureCount",
  "rowSource",
  "rowCount",
  "orientation",
  "pointLabels",
  "exposure",
  "baseline",
] as const satisfies ReadonlyArray<keyof IntakeTargetShape>;

const TARGET_SHAPE_VOCABULARIES = {
  IntakeQuestionTypeHint: [
    "matrix",
    "matrix_rating",
    "matrix_categorical",
    "rating",
    "single_select",
    "multi_select",
    "open_end",
    "ranking",
    "number",
    "continuous",
  ],
  IntakeRowSourceHint: ["fixed", "tested_assets"],
  IntakeOrientationHint: ["rows_are_subjects", "rows_are_measures"],
  IntakeExposureHint: ["monadic", "comparison"],
} as const satisfies {
  IntakeQuestionTypeHint: ReadonlyArray<IntakeQuestionTypeHint>;
  IntakeRowSourceHint: ReadonlyArray<IntakeRowSourceHint>;
  IntakeOrientationHint: ReadonlyArray<IntakeOrientationHint>;
  IntakeExposureHint: ReadonlyArray<IntakeExposureHint>;
};

const PLAN_VERBATIM_FIELDS = [
  "questionText",
  "optionLabels",
  "columnOptionLabels",
  "rowLabels",
] as const satisfies ReadonlyArray<keyof PlanVerbatimContent>;

const POINT_LABEL_FIELDS = ["presence", "count"] as const;

const hasOwn = (value: Record<string, unknown>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(value, key);

function isRecordWithFields(
  value: unknown,
  allowed: ReadonlyArray<string>,
  required: ReadonlyArray<string> = [],
): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.keys(value).every((key) => allowed.includes(key)) &&
    required.every((key) => hasOwn(value as Record<string, unknown>, key))
  );
}

function isVocabulary(
  value: unknown,
  vocabulary: ReadonlyArray<string>,
): value is string {
  return typeof value === "string" && vocabulary.includes(value);
}

function isInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value);
}

function isStringArray(value: unknown): value is Array<string> {
  return (
    Array.isArray(value) && value.every((item) => typeof item === "string")
  );
}

function optionalField(
  value: Record<string, unknown>,
  key: string,
  validator: (candidate: unknown) => boolean,
): boolean {
  return !hasOwn(value, key) || validator(value[key]);
}

function isPlanVerbatimContent(value: unknown): value is PlanVerbatimContent {
  return (
    isRecordWithFields(value, PLAN_VERBATIM_FIELDS) &&
    optionalField(
      value,
      "questionText",
      (candidate) => typeof candidate === "string",
    ) &&
    optionalField(value, "optionLabels", isStringArray) &&
    optionalField(
      value,
      "columnOptionLabels",
      (candidate) => Array.isArray(candidate) && candidate.every(isStringArray),
    ) &&
    optionalField(value, "rowLabels", isStringArray)
  );
}

function isIntakePointLabelIntent(
  value: unknown,
): value is IntakePointLabelIntent {
  if (
    !isRecordWithFields(value, POINT_LABEL_FIELDS, ["presence"]) ||
    (value.presence !== "removed" && value.presence !== "stated") ||
    !optionalField(
      value,
      "count",
      (candidate) => isInteger(candidate) && candidate > 0,
    )
  ) {
    return false;
  }
  return value.presence === "stated" || !hasOwn(value, "count");
}

function isIntakeTargetShape(value: unknown): value is IntakeTargetShape {
  return (
    isRecordWithFields(value, TARGET_SHAPE_FIELDS) &&
    optionalField(value, "questionType", (candidate) =>
      isVocabulary(candidate, TARGET_SHAPE_VOCABULARIES.IntakeQuestionTypeHint),
    ) &&
    optionalField(
      value,
      "questionCount",
      (candidate) => isInteger(candidate) && candidate > 0,
    ) &&
    optionalField(
      value,
      "columnCount",
      (candidate) => isInteger(candidate) && candidate > 0,
    ) &&
    optionalField(
      value,
      "measureCount",
      (candidate) => isInteger(candidate) && candidate > 0,
    ) &&
    optionalField(value, "rowSource", (candidate) =>
      isVocabulary(candidate, TARGET_SHAPE_VOCABULARIES.IntakeRowSourceHint),
    ) &&
    optionalField(
      value,
      "rowCount",
      (candidate) => isInteger(candidate) && candidate > 0,
    ) &&
    optionalField(value, "orientation", (candidate) =>
      isVocabulary(candidate, TARGET_SHAPE_VOCABULARIES.IntakeOrientationHint),
    ) &&
    optionalField(value, "pointLabels", isIntakePointLabelIntent) &&
    optionalField(value, "exposure", (candidate) =>
      isVocabulary(candidate, TARGET_SHAPE_VOCABULARIES.IntakeExposureHint),
    ) &&
    optionalField(
      value,
      "baseline",
      (candidate) => typeof candidate === "boolean",
    )
  );
}

/** A stored constraint's shape: non-empty, with no exposure or baseline. */
function isEvidenceTargetShape(
  value: unknown,
): value is IntakeAuthoringTargetShape {
  return (
    isIntakeTargetShape(value) &&
    Object.keys(value).length > 0 &&
    value.exposure === undefined &&
    value.baseline === undefined
  );
}

function exactKeys(
  candidate: Record<string, unknown>,
  expected: ReadonlyArray<string>,
): boolean {
  const actual = Object.keys(candidate).sort();
  return (
    actual.length === expected.length &&
    actual.every((key, index) => key === [...expected].sort()[index])
  );
}

function nonBlank(candidate: unknown, max: number): candidate is string {
  return (
    typeof candidate === "string" &&
    candidate.trim().length > 0 &&
    candidate.length <= max
  );
}

/** One requirement of the contract, read under its `schemaVersion`. */
function isEvidenceRequirement(
  candidate: unknown,
  schemaVersion: unknown,
): candidate is StudyEvidenceRequirement {
  if (
    typeof candidate !== "object" ||
    candidate === null ||
    Array.isArray(candidate)
  ) {
    return false;
  }
  const item = candidate as Record<string, unknown>;
  return (
    Object.keys(item).every((key) =>
      [
        "id",
        "statement",
        "sourceText",
        "requiredness",
        "topicCardinality",
        "reporting",
        "answerKind",
        "allowedOutcomes",
        "role",
        "subjectScope",
        "subjectAssetType",
        "applicabilityRequirementId",
      ].includes(key),
    ) &&
    [
      "id",
      "statement",
      "sourceText",
      "requiredness",
      "topicCardinality",
      "reporting",
    ].every((key) => Object.prototype.hasOwnProperty.call(item, key)) &&
    nonBlank(item.id, 120) &&
    nonBlank(item.statement, 800) &&
    nonBlank(item.sourceText, 800) &&
    ["required", "optional", "unspecified"].includes(
      item.requiredness as string,
    ) &&
    ["one", "one_or_more"].includes(item.topicCardinality as string) &&
    ["report", "survey_data_only", "unspecified"].includes(
      item.reporting as string,
    ) &&
    (item.answerKind === undefined ||
      [
        "choice",
        "multiple_choice",
        "scale",
        "number",
        "open_text",
        "ranking",
      ].includes(item.answerKind as string)) &&
    (item.allowedOutcomes === undefined ||
      (Array.isArray(item.allowedOutcomes) &&
        item.allowedOutcomes.every((outcome) =>
          [
            "none",
            "cannot_tell",
            "not_applicable",
            "prefer_not_to_answer",
          ].includes(outcome as string),
        ) &&
        new Set(item.allowedOutcomes).size ===
          item.allowedOutcomes.length)) &&
    (item.role === undefined ||
      [
        "context",
        "occurrence",
        "evaluation",
        "selection",
        "explanation",
        "other",
      ].includes(item.role as string)) &&
    (item.subjectScope === undefined ||
      [
        "study",
        "each_named_brand",
        "each_tested_asset",
        "tested_asset_set",
      ].includes(item.subjectScope as string)) &&
    (item.subjectAssetType === undefined ||
      TESTED_ASSET_TYPE_IDS.includes(item.subjectAssetType as never)) &&
    (item.applicabilityRequirementId === undefined ||
      nonBlank(item.applicabilityRequirementId, 120)) &&
    (schemaVersion !== "study-evidence-contract.v3" ||
      (item.role !== undefined && item.subjectScope !== undefined)) &&
    !(
      ["context", "occurrence", "evaluation"].includes(item.role as string) &&
      Array.isArray(item.allowedOutcomes) &&
      item.allowedOutcomes.includes("none")
    ) &&
    !(
      ["study", "each_named_brand"].includes(item.subjectScope as string) &&
      item.subjectAssetType !== undefined
    ) &&
    !(
      ["each_tested_asset", "tested_asset_set"].includes(
        item.subjectScope as string,
      ) && item.subjectAssetType === undefined
    )
  );
}
/** One constraint of the contract. */
function isEvidenceConstraint(
  candidate: unknown,
): candidate is StudyEvidenceConstraint {
  if (
    typeof candidate !== "object" ||
    candidate === null ||
    Array.isArray(candidate)
  ) {
    return false;
  }
  const item = candidate as Record<string, unknown>;
  return (
    Object.keys(item).every((key) =>
      [
        "id",
        "statement",
        "sourceText",
        "scopeRequirementIds",
        "targetShape",
        "verbatimContent",
      ].includes(key),
    ) &&
    ["id", "statement", "sourceText"].every((key) =>
      Object.prototype.hasOwnProperty.call(item, key),
    ) &&
    nonBlank(item.id, 120) &&
    nonBlank(item.statement, 800) &&
    nonBlank(item.sourceText, 800) &&
    (item.targetShape === undefined ||
      isEvidenceTargetShape(item.targetShape)) &&
    (item.verbatimContent === undefined ||
      isPlanVerbatimContent(item.verbatimContent)) &&
    (item.scopeRequirementIds === undefined ||
      (Array.isArray(item.scopeRequirementIds) &&
        item.scopeRequirementIds.every((id) => nonBlank(id, 120)) &&
        new Set(item.scopeRequirementIds).size ===
          item.scopeRequirementIds.length))
  );
}

/**
 * Structural validator for the shared evidence contract.
 *
 * It validates only shape, closed vocabularies and stable identity. Language
 * interpretation remains in contextual intake; exact source grounding is
 * checked there against the authoritative user messages.
 */
export function isStudyEvidenceContract(
  value: unknown,
): value is StudyEvidenceContract {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const record = value as Record<string, unknown>;
  const schemaVersion = record.schemaVersion;
  if (
    !exactKeys(record, [
      "schemaVersion",
      "expansionPolicy",
      "requirements",
      "constraints",
    ]) ||
    ![
      "study-evidence-contract.v1",
      "study-evidence-contract.v2",
      "study-evidence-contract.v3",
    ].includes(record.schemaVersion as string) ||
    !["requested_only", "recommendations_allowed"].includes(
      record.expansionPolicy as string,
    ) ||
    !Array.isArray(record.requirements) ||
    record.requirements.length > 18 ||
    !record.requirements.every((entry) =>
      isEvidenceRequirement(entry, schemaVersion),
    ) ||
    !Array.isArray(record.constraints) ||
    record.constraints.length > 24 ||
    !record.constraints.every(isEvidenceConstraint) ||
    (record.requirements.length === 0 &&
      (!["study-evidence-contract.v2", "study-evidence-contract.v3"].includes(
        record.schemaVersion as string,
      ) ||
        record.constraints.length === 0))
  ) {
    return false;
  }
  const ids = [...record.requirements, ...record.constraints].map(
    (entry) => entry.id,
  );
  const requirementIds = new Set(record.requirements.map((entry) => entry.id));
  const applicabilityById = new Map(
    record.requirements.map((entry) => [
      entry.id,
      entry.applicabilityRequirementId,
    ]),
  );
  const requirementById = new Map(
    record.requirements.map((entry) => [entry.id, entry]),
  );
  const dependenciesAreValid = record.requirements.every((entry) => {
    const dependency = entry.applicabilityRequirementId;
    if (
      dependency === undefined ||
      (!requirementIds.has(dependency) && dependency !== entry.id)
    ) {
      return dependency === undefined;
    }
    const seen = new Set([entry.id]);
    let cursor: string | undefined = dependency;
    while (cursor !== undefined) {
      if (seen.has(cursor)) return false;
      seen.add(cursor);
      cursor = applicabilityById.get(cursor);
    }
    return true;
  });
  const dependentEvaluationsAreAnswerable = record.requirements.every(
    (entry) => {
      if (
        entry.role !== "evaluation" ||
        entry.requiredness !== "required" ||
        entry.applicabilityRequirementId === undefined
      ) {
        return true;
      }
      const dependency = requirementById.get(entry.applicabilityRequirementId);
      return (
        dependency === undefined ||
        !["context", "occurrence"].includes(dependency.role as string) ||
        entry.allowedOutcomes?.includes("not_applicable") === true
      );
    },
  );
  return (
    new Set(ids).size === ids.length &&
    (schemaVersion !== "study-evidence-contract.v3" ||
      (dependenciesAreValid && dependentEvaluationsAreAnswerable)) &&
    record.constraints.every((entry) =>
      (entry.scopeRequirementIds ?? []).every((id) => requirementIds.has(id)),
    )
  );
}
